import {
  addQ,
  mulQ,
  type QuadNumber,
  quadFromRational,
  sqrtRational,
} from "@/lib/tool-kit/linear-algebra/quad-number";
import { absBig, BIG_ONE, BIG_ZERO, gcdBig } from "@/lib/tool-kit/math/bigint";
import { simplifySqrt } from "@/lib/tool-kit/math/radical";
import {
  addR,
  commonDenominator,
  compareR,
  divR,
  fromBigInt,
  intR,
  mulR,
  type Rational,
  signR,
  subR,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/**
 * The Pythagorean theorem, a² + b² = c², solved for the missing side and run backwards
 * to classify a triangle — all in exact arithmetic, so a hypotenuse of √72 is 6√2 and a
 * 1.5-2-2.5 triangle is recognised as the 3-4-5 triple scaled by one half.
 */

export type Unknown = "a" | "b" | "c";

export interface RightTriangle {
  unknown: Unknown;
  /** The two sides that were entered, as the rationals typed. */
  known: { name: Unknown; value: Rational }[];
  /** The radicand of the solved side: a² + b² for the hypotenuse, c² − b² for a leg. */
  radicand: Rational;
  a: QuadNumber;
  b: QuadNumber;
  c: QuadNumber;
  /** Decimal values of the three sides. */
  decimals: { a: number; b: number; c: number };
  area: QuadNumber;
  perimeter: QuadNumber;
  triple: TripleInfo | null;
}

export type SolveError = "not-positive" | "leg-too-long";

const square = (x: Rational) => mulR(x, x);

/** Solve for the missing side given the other two. */
export function solveRightTriangle(
  unknown: Unknown,
  first: Rational,
  second: Rational
): RightTriangle | SolveError {
  if (signR(first) <= 0 || signR(second) <= 0) {
    return "not-positive";
  }
  // `first` and `second` are the two named sides in alphabetical order.
  const names = (["a", "b", "c"] as const).filter((n) => n !== unknown);
  const [n1, n2] = names;
  const sides: Partial<Record<Unknown, Rational>> = {
    [n1]: first,
    [n2]: second,
  };
  let radicand: Rational;
  if (unknown === "c") {
    radicand = addR(square(first), square(second));
  } else {
    const hyp = sides.c as Rational;
    const leg = (unknown === "a" ? sides.b : sides.a) as Rational;
    radicand = subR(square(hyp), square(leg));
    if (signR(radicand) <= 0) {
      return "leg-too-long";
    }
  }
  const solved = sqrtRational(radicand);
  const value = (name: Unknown): QuadNumber =>
    name === unknown ? solved : quadFromRational(sides[name] as Rational);
  const a = value("a");
  const b = value("b");
  const c = value("c");
  const decimals = {
    a: toNum(a),
    b: toNum(b),
    c: toNum(c),
  };
  const rationalSides = [a, b, c].every((s) => s.b.num === BIG_ZERO);
  return {
    unknown,
    known: [
      { name: n1, value: first },
      { name: n2, value: second },
    ],
    radicand,
    a,
    b,
    c,
    decimals,
    area: mulQ(mulQ(a, b), quadFromRational(divR(intR(1), intR(2)))),
    perimeter: addQ(addQ(a, b), c),
    triple: rationalSides ? tripleOf(a.a, b.a, c.a) : null,
  };
}

function toNum(x: QuadNumber): number {
  return toNumberR(x.a) + toNumberR(x.b) * Math.sqrt(Number(x.d));
}

/* -------------------------------------------------------------------- triples */

export interface TripleInfo {
  /** The primitive triple with legs ascending: (3, 4, 5). */
  primitive: [bigint, bigint, bigint];
  /** The sides are the primitive triple times this factor. */
  factor: Rational;
  /** True when the sides themselves are whole numbers. */
  integers: boolean;
}

/** For rational sides that satisfy a² + b² = c²: the primitive triple they scale from. */
export function tripleOf(
  a: Rational,
  b: Rational,
  c: Rational
): TripleInfo | null {
  if (compareR(addR(square(a), square(b)), square(c)) !== 0) {
    return null;
  }
  const lcd = commonDenominator([a, b, c]);
  const ints = [a, b, c].map((s) => (s.num * lcd) / s.den);
  const g = ints.reduce((acc, x) => gcdBig(acc, x), BIG_ZERO);
  const [pa, pb, pc] = ints.map((x) => x / g);
  const legs = pa <= pb ? [pa, pb] : [pb, pa];
  const factor = divR(a, fromBigInt(pa));
  return {
    primitive: [legs[0], legs[1], pc],
    factor,
    integers: [a, b, c].every((s) => s.den === BIG_ONE),
  };
}

/* ------------------------------------------------------------- converse test */

export type TriangleKind = "right" | "acute" | "obtuse";

export interface Classification {
  /** Sides in ascending order. */
  sorted: [Rational, Rational, Rational];
  valid: boolean;
  kind: TriangleKind | null;
  /** a² + b² and c² for the two shorter sides and the longest. */
  sumOfSquares: Rational;
  longestSquared: Rational;
  triple: TripleInfo | null;
}

/** The converse: three sides make a right, acute or obtuse triangle by comparing a² + b² with c². */
export function classifyTriangle(
  sides: [Rational, Rational, Rational]
): Classification | "not-positive" {
  if (sides.some((s) => signR(s) <= 0)) {
    return "not-positive";
  }
  const sorted = [...sides].sort(compareR) as [Rational, Rational, Rational];
  const [a, b, c] = sorted;
  const sumOfSquares = addR(square(a), square(b));
  const longestSquared = square(c);
  const valid = compareR(addR(a, b), c) > 0;
  let kind: TriangleKind | null = null;
  if (valid) {
    const order = compareR(sumOfSquares, longestSquared);
    if (order === 0) {
      kind = "right";
    } else {
      kind = order > 0 ? "acute" : "obtuse";
    }
  }
  return {
    sorted,
    valid,
    kind,
    sumOfSquares,
    longestSquared,
    triple: kind === "right" ? tripleOf(a, b, c) : null,
  };
}

/* --------------------------------------------------------- radical working */

export type RadicalNote =
  | { kind: "perfect"; root: bigint }
  | { kind: "simplified"; coefficient: bigint; radicand: bigint }
  | { kind: "simplest" };

/** How √n simplifies, for the "simplify the radical" line of the working. */
export function radicalNote(n: bigint): RadicalNote {
  const root = simplifySqrt(absBig(n));
  if (root.radicand === BIG_ONE) {
    return { kind: "perfect", root: root.coefficient };
  }
  if (root.coefficient === BIG_ONE) {
    return { kind: "simplest" };
  }
  return {
    kind: "simplified",
    coefficient: root.coefficient,
    radicand: root.radicand,
  };
}
